/**
 * sampleRowSort
 * ================================================================
 * Pure helpers for ranking and truncating parsed sample rows.
 *
 * The authoring Sample Data tab shows up to 100 rows from the parsed
 * file, sorted so the rows with the fewest empty/null cells come
 * first. Authors benefit from seeing the richest examples up top
 * because that's where they can validate column detection, spot type
 * issues, and give the AI suggester the most signal.
 *
 * Kept in its own module (not co-located with the component) so the
 * behavior can be unit-tested without rendering JSX and without
 * bringing React into the test runner for a pure data operation.
 */

/**
 * Count how many of the specified columns are null, undefined, or
 * empty-string in the given row. Whitespace-only strings still count
 * as empty so imports with whitespace-pollution don't float to the
 * top of the preview.
 */
export function countMissingCells(
  row: Record<string, unknown>,
  columns: readonly string[],
): number {
  let n = 0;
  for (const c of columns) {
    const v = row?.[c];
    if (v === null || v === undefined) {
      n++;
      continue;
    }
    if (typeof v === 'string' && v.trim() === '') {
      n++;
    } else if (v === '') {
      n++;
    }
  }
  return n;
}

/**
 * Return up to `limit` rows from `rows`, ordered by completeness
 * (fewest missing cells first). Ties preserve original file order
 * \u2014 callers rely on this for a stable, deterministic preview.
 *
 * The slice is applied BEFORE the sort so the ranking only considers
 * the rows we're actually going to display. That matches the
 * authoring UX contract ("show the most complete of the first 100
 * rows", NOT "scan the entire file for the 100 most complete").
 *
 * When the column list is empty the function returns the first
 * `limit` rows unsorted \u2014 there's nothing to rank against.
 */
export function sortRowsByCompleteness(
  rows: readonly Record<string, unknown>[],
  columns: readonly string[],
  limit: number,
): Record<string, unknown>[] {
  const base = rows.slice(0, limit);
  if (base.length === 0 || columns.length === 0) return [...base];
  return base
    .map((row, i) => ({ row, i, miss: countMissingCells(row, columns) }))
    .sort((a, b) => (a.miss - b.miss) || (a.i - b.i))
    .map(x => x.row);
}
